// DAEMON — the visible leaderboard.
//
// `leaderboard.ts` is the wire; this is the window you open onto it. One
// self-contained panel: category tabs, a difficulty filter, a daily /
// all-seeds toggle, and a ranked table where every row carries a proof
// link.
//
// Four design rules, all of them load-bearing:
//
//   1. **The empty state is the default state.** The API is new and the
//      board is genuinely empty today, so "nobody has done this yet" is
//      the screen most visitors will actually see. It is written as an
//      invitation with the exact unclaimed slot named, not as an error
//      and not as "no data".
//
//   2. **"Empty" and "down" are different sentences.** `fetchBoard`
//      resolves to `[]` on any failure, so an unreachable API and a
//      board nobody has posted to are indistinguishable by return value.
//      That is the worst possible misreport — a dead service reading as
//      "nobody is playing" — so we probe with `fetchMeta()` first and
//      only call a board empty when something answered. If nothing
//      answered we say so and offer a retry, and we never guess.
//
//   3. **Every row can be checked.** The whole point of this board is
//      that the server proved each time by re-running the input stream.
//      `fetchReplay(id)` returns that stream, so each row gets a proof
//      button that fetches it and reports its size. A claim you cannot
//      pull the evidence for is a claim, not a record.
//
//   4. **Density, and it never scrolls the page sideways.** Arcade
//      high-score table crossed with a splits window: monospace,
//      `tabular-nums` on every column with digits, hairlines instead of
//      whitespace. The table scrolls inside its own box.
//
// Pure presentation plus its own fetches. It owns no game state, writes
// nothing to storage and cannot change a number, which is what makes it
// safe to open mid-run. It imports nothing from `src/shell`, `src/apps`
// or `src/lib/wm`, because it ships in both entries — the skenOS app and
// standalone daemon.skenmy.com.
//
// Styling: prefix `dbd-`, in the "DAEMON — leaderboard" section of
// `daemon.css`.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { dailyRun, NICE_LEVELS, normalizeSeed } from "./daily";
import {
  fetchBoard,
  fetchMeta,
  fetchReplay,
  fetchSeasons,
  type BoardRow,
  type Season,
} from "./leaderboard";
import { CATEGORIES, formatDelta, formatTicks, type CategoryId } from "./records";
import { upgradeById } from "./upgrades";
import { ENGINE_VERSION } from "./replay";

// ── Formatting ────────────────────────────────────────────────────────

const niceLabel = (n: number): string => `nice ${n > 0 ? `+${n}` : n}`;

/** Kindest and cruellest get a word, because "+19" and "-20" do not tell
 *  a first-time visitor which end of the scale is the hard one. */
function niceOption(n: number): string {
  if (n === NICE_LEVELS[0]) return `${niceLabel(n)} · kindest`;
  if (n === NICE_LEVELS[NICE_LEVELS.length - 1]) return `${niceLabel(n)} · cruellest`;
  return niceLabel(n);
}

/** Proof size. Bytes below a kilobyte, because "0.0 kB" reads as
 *  nothing arriving when in fact something did. */
function formatBytes(n: number): string {
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} kB`;
}

/** The board stores an ISO timestamp; the table has room for a date. */
function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toISOString().slice(0, 10);
}

/** Whether the active category ranks on points rather than time. The
 *  board is otherwise a speedrun table, and this is the one column set
 *  that reads the other way round. */
const ranksByScore = (id: CategoryId): boolean =>
  CATEGORIES.find((c) => c.id === id)?.rankBy === "score";

const initialsOf = (row: BoardRow): string => (row.initials || "???").slice(0, 3).toUpperCase();

// ── State ─────────────────────────────────────────────────────────────

/** `down` is not an error state in the "something threw" sense — it is a
 *  claim about the world that we are entitled to make only because
 *  `fetchMeta` failed. Keeping it a distinct phase from `ready` with
 *  zero rows is rule 2, in the type system. */
type Phase =
  | { kind: "loading" }
  | { kind: "down" }
  | { kind: "ready"; rows: BoardRow[]; engine: number; compatible: boolean };

type Proof =
  | { kind: "checking" }
  | { kind: "ok"; bytes: number }
  | { kind: "missing" };

const SKELETON_ROWS = 6;
const ROW_LIMIT = 25;

function coerceCategory(id: string | undefined): CategoryId {
  return CATEGORIES.find((c) => c.id === id)?.id ?? "any";
}

/** `initialNice` arrives as a plain `number` from callers that got it
 *  from a query string or a finished run. Anything not on the ladder is
 *  treated as absent rather than clamped — guessing at what someone
 *  meant by `nice=7` silently shows them a different board. */
function coerceNice(n: number | undefined): number | null {
  if (n === undefined) return null;
  return NICE_LEVELS.find((v) => v === n) ?? null;
}

// ── Panel ─────────────────────────────────────────────────────────────

export function LeaderboardPanel({
  initialCategory,
  initialNice,
  seed,
  onClose,
}: {
  initialCategory?: string;
  initialNice?: number;
  seed?: string;
  onClose?: () => void;
}): JSX.Element {
  // Read the clock exactly once. A panel left open across UTC midnight
  // showing yesterday's seed labelled "today" is a smaller lie than one
  // that silently swaps the board out from under a reader mid-scroll.
  const today = useMemo(() => dailyRun(), []);

  // The seed this panel is *about*: whatever the caller handed us (the
  // run just finished, usually) or today's daily. The daily board is the
  // socially interesting one, so it is the default view.
  const focusSeed = useMemo(
    () => (seed ? normalizeSeed(seed) : today.seed),
    [seed, today.seed],
  );
  const isDaily = focusSeed === today.seed;

  const [category, setCategory] = useState<CategoryId>(() => coerceCategory(initialCategory));
  const [scope, setScope] = useState<"seed" | "all">("seed");
  const [nice, setNice] = useState<number | null>(
    () => coerceNice(initialNice) ?? (isDaily ? today.nice : null),
  );
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [proofs, setProofs] = useState<Record<string, Proof>>({});
  const [attempt, setAttempt] = useState(0);
  const [seasons, setSeasons] = useState<Season[]>([]);
  /** Which season is being read. Null means the current one. A past
   *  season is archived, not deleted — the runs happened and were
   *  verified when posted, and hiding them behind a version bump would
   *  make the board a rolling scoreboard rather than a record. */
  const [season, setSeason] = useState<number | null>(null);

  const activeSeed = scope === "seed" ? focusSeed : undefined;
  const byScore = ranksByScore(category);

  // Escape closes. The panel opens over a running game, so the key that
  // gets you out of everything else has to get you out of this.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // One fetch per view. `fetchMeta` is doing double duty: it tells us
  // what the board will accept, and — because it is the only call here
  // that can distinguish "answered" from "did not" — it is the
  // reachability probe that keeps an outage from rendering as an empty
  // leaderboard.
  useEffect(() => {
    let live = true;
    setPhase({ kind: "loading" });
    setProofs({});
    void (async () => {
      const [meta, rows, allSeasons] = await Promise.all([
        fetchMeta(),
        fetchBoard({
          category,
          nice: nice ?? undefined,
          seed: activeSeed,
          limit: ROW_LIMIT,
          engine: season ?? undefined,
        }),
        fetchSeasons(),
      ]);
      if (live) setSeasons(allSeasons);
      if (!live) return;
      if (!meta) {
        setPhase({ kind: "down" });
        return;
      }
      setPhase({ kind: "ready", rows, engine: meta.engine, compatible: meta.compatible });
    })();
    return () => {
      live = false;
    };
  }, [category, nice, activeSeed, attempt, season]);

  // Fetching a proof is a real network call and a double-click should
  // not make two of them. The ref is the guard the state can't be,
  // because the state update hasn't landed when the second click fires.
  const inFlight = useRef<Set<string>>(new Set());
  const verify = useCallback(async (id: string) => {
    if (inFlight.current.has(id)) return;
    inFlight.current.add(id);
    setProofs((p) => ({ ...p, [id]: { kind: "checking" } }));
    const replay = await fetchReplay(id);
    inFlight.current.delete(id);
    setProofs((p) => ({
      ...p,
      [id]: replay === null ? { kind: "missing" } : { kind: "ok", bytes: replay.length },
    }));
  }, []);

  const cat = CATEGORIES.find((c) => c.id === category) ?? CATEGORIES[0]!;
  const rows = phase.kind === "ready" ? phase.rows : [];
  const leader = rows[0];

  const scopeLabel = isDaily ? `today's seed · ${focusSeed}` : `this seed · ${focusSeed}`;
  const filterLine = `${cat.name} · ${nice === null ? "all difficulties" : niceLabel(nice)} · ${
    scope === "seed" ? focusSeed : "all seeds"
  }`;

  return (
    <section className="dbd" aria-label="DAEMON leaderboard">
      <header className="dbd-head">
        <div className="dbd-head-l">
          <h2 className="dbd-title">LEADERBOARD</h2>
          <p className="dbd-sub">
            Every time here was proved by the server re-running the input stream. Nothing on this
            board is taken on trust — pull the proof on any row and check it yourself.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="dbd-close" onClick={onClose}>
            CLOSE <kbd>esc</kbd>
          </button>
        ) : null}
      </header>

      {/* --- categories --- */}
      <div className="dbd-tabs" role="tablist" aria-label="Category">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            id={`dbd-tab-${c.id}`}
            aria-selected={c.id === category}
            aria-controls="dbd-board"
            className="dbd-tab"
            data-active={c.id === category ? "yes" : "no"}
            onClick={() => setCategory(c.id)}
          >
            {c.name}
          </button>
        ))}
      </div>
      <p className="dbd-blurb">{cat.blurb}</p>

      {/* --- filters --- */}
      {season !== null ? (
        <p className="dbd-archived" data-kind="archived">
          <b>Season v{season} — closed.</b> These runs were verified when they were posted,
          against the simulation as it stood then. Nothing here was deleted when the engine
          moved on; it simply stopped being comparable, so it stopped being ranked alongside
          the current one.
        </p>
      ) : null}

      <div className="dbd-filters">
        <div className="dbd-seg" role="group" aria-label="Which seeds">
          <button
            type="button"
            className="dbd-seg-b"
            data-active={scope === "seed" ? "yes" : "no"}
            aria-pressed={scope === "seed"}
            onClick={() => setScope("seed")}
          >
            {scopeLabel}
          </button>
          <button
            type="button"
            className="dbd-seg-b"
            data-active={scope === "all" ? "yes" : "no"}
            aria-pressed={scope === "all"}
            onClick={() => setScope("all")}
          >
            all seeds
          </button>
        </div>

        <label className="dbd-field">
          <span className="dbd-field-k">difficulty</span>
          <select
            className="dbd-select"
            value={nice === null ? "all" : String(nice)}
            onChange={(e) => setNice(e.target.value === "all" ? null : Number(e.target.value))}
          >
            <option value="all">all difficulties</option>
            {NICE_LEVELS.map((n) => (
              <option key={n} value={String(n)}>
                {niceOption(n)}
              </option>
            ))}
          </select>
        </label>

        {/* Season. Only rendered once there is history to read — a
            selector with one entry is noise, and on a board's first
            season there is nothing to go back to. */}
        {seasons.length > 1 ? (
          <select
            className="dbd-season"
            aria-label="Season"
            value={season ?? ""}
            onChange={(e) => setSeason(e.target.value === "" ? null : Number(e.target.value))}
          >
            <option value="">this season · engine v{ENGINE_VERSION}</option>
            {seasons
              .filter((sn) => sn.engine !== ENGINE_VERSION)
              .map((sn) => (
                <option key={sn.engine} value={sn.engine}>
                  season v{sn.engine} · {sn.runs} run{sn.runs === 1 ? "" : "s"} · archived
                </option>
              ))}
          </select>
        ) : null}
        <button
          type="button"
          className="dbd-refresh"
          onClick={() => setAttempt((a) => a + 1)}
          disabled={phase.kind === "loading"}
        >
          refresh
        </button>
      </div>

      {isDaily && scope === "seed" ? (
        <p className="dbd-note">
          Today's daily: <b>{focusSeed}</b> at {niceLabel(today.nice)}, UTC {today.day}. Same fight
          for everybody, which is the only reason these times are comparable at all.
        </p>
      ) : null}

      {/* --- engine mismatch --- */}
      {phase.kind === "ready" && !phase.compatible ? (
        <p className="dbd-alert" role="status" data-kind="engine">
          <b>Engine mismatch.</b> This build runs the simulation at v{ENGINE_VERSION}; the board
          verifies v{phase.engine}. A run submitted from here would be refused after the fact, so
          don't spend one on it. The times below still stand — they were proved against the board's
          engine, not this one.
        </p>
      ) : null}

      {/* --- the board --- */}
      <div className="dbd-board" id="dbd-board" role="tabpanel" aria-labelledby={`dbd-tab-${category}`}>
        <div className="dbd-status" aria-live="polite">
          {phase.kind === "loading" ? <span className="dbd-vh">Loading the board…</span> : null}
        </div>

        {phase.kind === "loading" ? <Skeleton /> : null}

        {phase.kind === "down" ? (
          <div className="dbd-state" data-kind="down" role="status">
            <p className="dbd-state-h">The board is not answering.</p>
            <p>
              That is a failure to reach it, not an empty leaderboard. The two look identical over
              the wire and this screen refuses to guess between them, because telling you nobody is
              playing when in fact nothing asked is the worse of the two lies.
            </p>
            <p>Your runs are recorded locally either way. Nothing about playing depends on this.</p>
            <button type="button" className="dbd-retry" onClick={() => setAttempt((a) => a + 1)}>
              try again
            </button>
          </div>
        ) : null}

        {phase.kind === "ready" && rows.length === 0 ? (
          <div className="dbd-state" data-kind="empty" role="status">
            <p className="dbd-state-h">Nobody has done this one yet.</p>
            <p>
              The board answered. It simply had nothing in it: <b>{filterLine}</b> is unclaimed, and
              the first row of this table is sitting there with your name not on it.
            </p>
            <p>
              Win it, put three letters on it, and it stays yours until somebody takes it off you —
              which they will have to actually do, on the same seed, at the same difficulty, with
              the server watching every input.
            </p>
          </div>
        ) : null}

        {phase.kind === "ready" && rows.length > 0 ? (
          <div className="dbd-scroll" tabIndex={0}>
            <table className="dbd-table">
              <thead>
                <tr>
                  <th scope="col" className="dbd-num">#</th>
                  <th scope="col">who</th>
                  <th scope="col" className="dbd-num">{byScore ? "score" : "time"}</th>
                  <th scope="col" className="dbd-num" title="Gap to the leader of this board">
                    Δ
                  </th>
                  <th scope="col" className="dbd-num">nice</th>
                  <th scope="col" className="dbd-num" title="Bullets grazed — how close it was run">
                    graze
                  </th>
                  <th scope="col" className="dbd-num" title="Hits taken">
                    hit
                  </th>
                  <th scope="col" className="dbd-num" title="Upgrades drafted">
                    mods
                  </th>
                  {scope === "all" ? (
                    <th scope="col">seed</th>
                  ) : null}
                  <th scope="col">set</th>
                  <th scope="col">proof</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const proof = proofs[row.id];
                  const gap = leader && row.rank !== leader.rank ? row.ticks - leader.ticks : null;
                  // Points behind the leader, not seconds. Positive
                  // number, rendered with a leading minus, because on
                  // this board you are *down* on the leader rather than
                  // slower than them.
                  const scoreGap =
                    leader && row.rank !== leader.rank ? leader.score - row.score : null;
                  return (
                    <tr key={row.id} data-rank={row.rank} data-clean={row.hitsTaken === 0 ? "yes" : "no"}>
                      <td className="dbd-num dbd-rank">{row.rank}</td>
                      {/* Initials stay the identity; a claimed name is
                          an annotation on it, not a replacement. That
                          keeps the board readable as an arcade table
                          and keeps unclaimed runs from looking like
                          second-class entries — they are not, and they
                          were verified exactly as hard. */}
                      <th scope="row" className="dbd-who">
                        {initialsOf(row)}
                        {row.runner ? (
                          <span className="dbd-runner" title={`claimed by ${row.runner.name}`}>
                            <i className="dbd-tick" aria-hidden="true">
                              ✓
                            </i>
                            {row.runner.name}
                          </span>
                        ) : null}
                        {row.daily ? <span className="dbd-chip dbd-chip--daily">daily</span> : null}
                      </th>
                      {byScore ? (
                        <>
                          <td className="dbd-num dbd-time">{row.score.toLocaleString()}</td>
                          <td className="dbd-num dbd-delta">
                            {scoreGap === null ? (
                              <span className="dbd-lead">leader</span>
                            ) : (
                              `-${scoreGap.toLocaleString()}`
                            )}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="dbd-num dbd-time">{formatTicks(row.ticks)}</td>
                          <td className="dbd-num dbd-delta">
                            {gap === null ? (
                              <span className="dbd-lead">leader</span>
                            ) : (
                              formatDelta(gap)
                            )}
                          </td>
                        </>
                      )}
                      <td className="dbd-num dbd-nice">{row.nice > 0 ? `+${row.nice}` : row.nice}</td>
                      <td className="dbd-num dbd-graze">{row.grazes}</td>
                      <td className="dbd-num dbd-hits">{row.hitsTaken}</td>
                      {/* The build, not a count of it. A number here
                          answers "did they draft", which nobody was
                          asking; the interesting question a board can
                          settle is *what the fast people ran*, and only
                          the actual cards answer that. Names, because
                          `kill -9` means something to this audience and
                          "4" does not. */}
                      <td className="dbd-mods">
                        {row.upgrades.length === 0 ? (
                          <span className="dbd-chip dbd-chip--none" title="drafted nothing">
                            none
                          </span>
                        ) : (
                          <span className="dbd-build">
                            {row.upgrades.map((id) => {
                              const u = upgradeById(id);
                              return (
                                <span
                                  key={id}
                                  className="dbd-chip"
                                  data-arch={u?.archetype ?? "unknown"}
                                  title={u ? `${u.name} — ${u.blurb}` : id}
                                >
                                  {u?.name ?? id}
                                </span>
                              );
                            })}
                          </span>
                        )}
                      </td>
                      {scope === "all" ? <td className="dbd-seed">{row.seed}</td> : null}
                      <td className="dbd-when">{shortDate(row.submittedAt)}</td>
                      <td className="dbd-proof">
                        <button
                          type="button"
                          className="dbd-proof-b"
                          data-state={proof?.kind ?? "idle"}
                          disabled={proof?.kind === "checking"}
                          onClick={() => void verify(row.id)}
                          aria-label={`Fetch the proof behind ${initialsOf(row)}'s ${formatTicks(row.ticks)}`}
                        >
                          {proof === undefined ? "proof" : null}
                          {proof?.kind === "checking" ? "…" : null}
                          {proof?.kind === "ok" ? `✓ ${formatBytes(proof.bytes)}` : null}
                          {proof?.kind === "missing" ? "no proof" : null}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <p className="dbd-foot">
        The client cannot post a time — there is nowhere in the submission to put one. It sends the
        replay and three initials, and the server re-simulates the run to decide what the time was.
        That is why "proof" on a row is a real file and not a badge.
      </p>
    </section>
  );
}

/** Skeleton rows, not a spinner: a spinner says "wait", a skeleton says
 *  "a ranked table is about to appear here", and the layout doesn't jump
 *  when it does. Purely decorative — the live region carries the word. */
function Skeleton(): JSX.Element {
  return (
    <div className="dbd-skel" aria-hidden="true">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <div className="dbd-skel-row" key={i} style={{ "--dbd-i": i } as CSSProperties}>
          <i className="dbd-skel-c" data-w="rank" />
          <i className="dbd-skel-c" data-w="who" />
          <i className="dbd-skel-c" data-w="time" />
          <i className="dbd-skel-c" data-w="rest" />
        </div>
      ))}
    </div>
  );
}
